import { useEffect, useState } from 'react'
import { getPlatform } from '@/lib/platform/platformDetection'
import { useQueueState } from '@/lib/helpers/bgSync/useSyncQueueStore'
import {
  decideUpdatePresentation,
  type QueueState,
  type UpdateDecision,
} from '@/lib/nativeUpdateTiers'
import type { NativeVersionUpdate } from '@/lib/nativeVersion'
import { useNativeVersionUpdateStore } from '@/store/useNativeVersionUpdateStore'

/** setTimeout truncates to a signed 32-bit delay; past this it fires at once. */
const MAX_TIMEOUT_MS = 2_147_483_647

/**
 * The queue state handed to the decision when the caller only wants to know
 * WHETHER something is on screen. Any member would do — see
 * `useIsNativeUpdateShowing` for why, and the pin that keeps it true.
 */
const VISIBILITY_PROBE_QUEUE_STATE: QueueState = 'empty'

export type NativeUpdateDecision = {
  decision: UpdateDecision
  availableUpdate: NativeVersionUpdate | null
  /** The instant the decision was taken, so callers gate on the same clock. */
  now: number
}

type DecisionInputs = {
  availableUpdate: NativeVersionUpdate | null
  firstSeenAt: number | null
  snoozedUntil: number | null
  snoozedVersion: string | null
  now: number
}

/**
 * Everything the tiering decision needs EXCEPT the queue, plus the clock wake-up
 * that makes a snooze expire on its own.
 */
function useDecisionInputs(): DecisionInputs {
  const availableUpdate = useNativeVersionUpdateStore(state => state.availableUpdate)
  const snoozedVersion = useNativeVersionUpdateStore(state => state.snoozedVersion)
  const persistedSnoozedUntil = useNativeVersionUpdateStore(state => state.snoozedUntil)
  const firstSeenByVersion = useNativeVersionUpdateStore(state => state.firstSeenByVersion)

  // A non-finite deadline is not a deadline. NaN makes `remaining <= 0` false and then hands
  // setTimeout a NaN delay, which it treats as 0 — an immediate re-arm, i.e. a render loop;
  // Infinity schedules forever and would hide the prompt permanently. Neither is reachable
  // through the persisted path today (sanitizeSnoozedUntil guards it), so this is insurance
  // against a future writer, not a fix for a live defect.
  const snoozedUntil =
    persistedSnoozedUntil !== null && Number.isFinite(persistedSnoozedUntil)
      ? persistedSnoozedUntil
      : null

  // A snooze expires by the CLOCK, and nothing else re-renders when it does: the store is not
  // touched at expiry, so a user who simply stays in the app would keep seeing nothing until
  // some unrelated render happened to land. Wake up at the deadline.
  //
  // The delay is clamped and the timer re-armed because setTimeout truncates its delay to a
  // signed 32-bit integer: anything beyond ~24.8 days fires IMMEDIATELY. The sanitized snooze
  // horizon is 30 days, i.e. squarely inside the range that breaks, so this is not a
  // theoretical guard. Waking too early is harmless here — the decision is recomputed from
  // the clock on every render — whereas waking 30 days late is the bug being fixed.
  const [clockTick, setClockTick] = useState(0)
  useEffect(() => {
    if (snoozedUntil === null) return
    const remaining = snoozedUntil - Date.now()
    if (remaining <= 0) return
    const timer = setTimeout(
      () => setClockTick(tick => tick + 1),
      Math.min(remaining, MAX_TIMEOUT_MS),
    )
    return () => clearTimeout(timer)
  }, [snoozedUntil, clockTick])

  return {
    availableUpdate,
    firstSeenAt: availableUpdate ? (firstSeenByVersion[availableUpdate.version] ?? null) : null,
    snoozedUntil,
    snoozedVersion,
    now: Date.now(),
  }
}

/**
 * The full native-update tiering decision: shared LOGIC, not a shared instance.
 *
 * This used to live inline in `NativeVersionUpdateCard`. It moved out because a
 * SECOND caller needs part of the same answer: the bottom-surface ladder has to
 * know whether the update surface is on screen, and the alternative was to
 * approximate it from the store.
 *
 * That approximation is the trap worth naming. `snooze()` does not clear
 * `availableUpdate` (see `useNativeVersionUpdateStore`), it only records a
 * deadline — so `availableUpdate !== null` stays true for as long as the
 * installed build is behind the store, which is days or weeks. Reading it as
 * "the update card is up" would have suppressed the push prompt for that entire
 * period, including the great majority of it when the card is snoozed and
 * nothing is on screen at all.
 *
 * Every hook here is per-caller, so keep this one to the caller that renders the
 * card: `useQueueState()` is not a plain selector, it forces a queue read and
 * registers a `nativeQueueManager` listener on mount. Callers that only need
 * visibility must use `useIsNativeUpdateShowing`, which does not touch the queue.
 */
export function useNativeUpdateDecision(): NativeUpdateDecision {
  const inputs = useDecisionInputs()
  const queueState = useQueueState()

  const decision = decideUpdatePresentation({
    platform: getPlatform(),
    ...inputs,
    queueState,
  })

  return { decision, availableUpdate: inputs.availableUpdate, now: inputs.now }
}

/**
 * Whether the native update is occupying the screen in ANY of its forms.
 *
 * Deliberately not "the bottom card": the blocking and interstitial tiers put a
 * modal dialog up instead, and a suggestion card left visible underneath one is
 * the dead-CTA defect this whole change removes.
 *
 * The queue is NOT read here, and that is a measured shortcut rather than an
 * assumption. `decideUpdatePresentation` returns `HIDDEN` from exactly two
 * places — non-native platform / no update, and an in-force snooze — and neither
 * looks at `queueState`; the queue only ever chooses WHICH visible form is used.
 * Reading it anyway would have put a forced IndexedDB read and a queue listener
 * behind every consumer of the ladder, on a WKWebView with a ~100MB ceiling.
 * `useNativeUpdateDecision.test.ts` pins the invariance, so a future edit that
 * makes visibility depend on the queue turns red here instead of silently
 * making this answer wrong.
 */
export function useIsNativeUpdateShowing(): boolean {
  const inputs = useDecisionInputs()

  const decision = decideUpdatePresentation({
    platform: getPlatform(),
    ...inputs,
    queueState: VISIBILITY_PROBE_QUEUE_STATE,
  })

  return decision.presentation !== 'hidden' && inputs.availableUpdate !== null
}
